import {
  FeedbackBody,
  type PlayableRecording,
  type RecordingName,
} from "@bandwith/recordings";
import { Button } from "@bandwith/ui";
import { Check, Megaphone, Pencil, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { FeedbackItem as FeedbackItemType } from "../lib/api";
import { api } from "../lib/api";
import { useSession } from "../lib/auth";
import {
  closesCard,
  type FeedbackCardSegment,
  opensCard,
} from "../lib/feedbackRuns";
import { FeedbackCodeMirror } from "./FeedbackCodeMirror";

type Props = {
  feedback: FeedbackItemType;
  /** Where this comment sits in a run of comments by one author */
  segment: FeedbackCardSegment;
  /** How long ago the comment was posted, formatted once for the whole list */
  relativeTime: string;
  /**
   * False when this row's time would only repeat the row above; the time is
   * then left out of the hairline but still given to screen readers, which
   * read each comment on its own.
   */
  printsTime: boolean;
  recordings: PlayableRecording[];
  /** What a recording chip calls its take */
  recordingName: RecordingName;
  isAdmin: boolean;
  /** Amplify controls only exist on song pages; omit to hide them */
  songAuthorId?: string | null;
  isAmplified?: boolean;
  onAmplifyToggle?: (feedbackId: string) => void;
  onFeedbackUpdated?: (id: string, body: string) => void;
  onFeedbackDeleted?: (id: string) => void;
  /** Called when a lyrics anchor chip is clicked */
  onLyricsAnchorClick?: (anchorText: string) => void;
};

/**
 * One comment, drawn as a whole card or as one segment of a run's card.
 *
 * The segment that opens a card carries the author line; later segments sit
 * under a hairline that carries at most the time. Each comment stays its own
 * element, keyed by id, so edit state survives when a new comment joins or
 * leaves its run.
 */
export function FeedbackItem({
  feedback,
  segment,
  relativeTime,
  printsTime,
  recordings,
  recordingName,
  isAdmin,
  songAuthorId = null,
  isAmplified = false,
  onAmplifyToggle,
  onFeedbackUpdated,
  onFeedbackDeleted,
  onLyricsAnchorClick,
}: Props) {
  const { session } = useSession();
  const currentUserId = session?.user?.id ?? null;

  const [isEditing, setIsEditing] = useState(false);
  const [editBody, setEditBody] = useState(feedback.body);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const deleteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isOwner = currentUserId === feedback.authorId;
  const canAmplify =
    onAmplifyToggle !== undefined &&
    currentUserId !== null &&
    (currentUserId === songAuthorId || isAdmin);

  useEffect(() => {
    return () => {
      if (deleteTimeoutRef.current) {
        clearTimeout(deleteTimeoutRef.current);
      }
    };
  }, []);

  const handleSave = async () => {
    if (!editBody.trim()) {
      setError("Feedback cannot be empty");
      return;
    }

    if (!currentUserId) {
      setError("You must be signed in to edit feedback");
      return;
    }

    const trimmedBody = editBody.trim();
    setIsPending(true);

    try {
      const response = await api.feedback[":id"].$patch({
        param: { id: feedback.id },
        json: { body: trimmedBody },
      });
      if (!response.ok) {
        setError("Failed to update feedback");
        return;
      }
      setError(null);
      onFeedbackUpdated?.(feedback.id, trimmedBody);
      setIsEditing(false);
    } catch {
      setError("Failed to update feedback");
    } finally {
      setIsPending(false);
    }
  };

  const handleDeleteClick = async () => {
    if (confirmingDelete) {
      if (deleteTimeoutRef.current) {
        clearTimeout(deleteTimeoutRef.current);
        deleteTimeoutRef.current = null;
      }
      if (!currentUserId) {
        setError("You must be signed in to delete feedback");
        setConfirmingDelete(false);
        return;
      }
      setIsPending(true);
      try {
        const response = await api.feedback[":id"].$delete({
          param: { id: feedback.id },
        });
        if (!response.ok) {
          setError("Failed to delete feedback");
          setConfirmingDelete(false);
          return;
        }
        onFeedbackDeleted?.(feedback.id);
      } catch {
        setError("Failed to delete feedback");
        setConfirmingDelete(false);
      } finally {
        setIsPending(false);
      }
    } else {
      setConfirmingDelete(true);
      deleteTimeoutRef.current = setTimeout(
        () => setConfirmingDelete(false),
        3000,
      );
    }
  };

  const handleCancel = () => {
    setEditBody(feedback.body);
    setIsEditing(false);
    setError(null);
  };

  const authorName = feedback.author.name || "Anonymous";
  const opens = opensCard(segment);
  const closes = closesCard(segment);

  const amplifyToggle = canAmplify ? (
    <button
      type="button"
      onClick={() => onAmplifyToggle?.(feedback.id)}
      disabled={isPending}
      className={`rounded p-1 transition-colors disabled:opacity-50 ${
        isAmplified
          ? "text-amber hover:bg-bg-tertiary hover:text-amber/70"
          : "text-text-muted hover:bg-bg-tertiary hover:text-amber"
      }`}
      aria-label={isAmplified ? "Remove amplification" : "Amplify comment"}
    >
      <Megaphone className="h-4 w-4" />
    </button>
  ) : null;

  const editControls =
    !isEditing && isOwner ? (
      <>
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          disabled={isPending}
          className="rounded p-1 text-text-muted transition-colors hover:bg-bg-tertiary hover:text-text-primary disabled:opacity-50"
          aria-label="Edit feedback"
        >
          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={handleDeleteClick}
          disabled={isPending}
          className={`rounded p-1 transition-colors disabled:opacity-50 ${
            confirmingDelete
              ? "bg-red-muted text-on-accent hover:bg-red-600"
              : "text-text-muted hover:bg-bg-tertiary hover:text-red-muted"
          }`}
          aria-label={confirmingDelete ? "Confirm delete" : "Delete feedback"}
        >
          {confirmingDelete ? (
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          )}
        </button>
      </>
    ) : null;

  // The card's fill and outline are drawn per segment: rounded and bordered
  // only on the edges where this segment opens or closes the card, so the
  // segments of a run meet flush and read as one card. Openers carry the gap
  // between cards; `relative` lets the song page's ring flash on one segment
  // paint above the segment below it.
  const shellClasses = [
    "relative border-x px-4",
    opens ? "mt-3 rounded-t-lg border-t pt-4 first:mt-0" : "",
    closes ? "rounded-b-lg border-b pb-4" : "pb-2.5",
    isAmplified
      ? "border-amber/30 bg-amber/5"
      : "border-border-subtle bg-bg-secondary",
  ].join(" ");

  return (
    <div
      id={`feedback-${feedback.id}`}
      data-testid="feedback-item"
      className={shellClasses}
    >
      {opens ? (
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            {amplifyToggle ? (
              <span className="-ml-1">{amplifyToggle}</span>
            ) : (
              isAmplified && (
                <Megaphone
                  className="h-4 w-4 text-amber"
                  aria-label="Amplified comment"
                />
              )
            )}
            {feedback.author.image ? (
              <img
                src={feedback.author.image}
                alt=""
                className="h-6 w-6 rounded-full"
              />
            ) : (
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-bg-tertiary text-xs text-text-muted">
                {feedback.author.name?.[0]?.toUpperCase() || "?"}
              </div>
            )}
            <span className="text-sm font-medium text-text-primary">
              {authorName}
            </span>
            <span className="text-xs text-text-muted">{relativeTime}</span>
          </div>

          {editControls && (
            <div className="flex items-center gap-1">{editControls}</div>
          )}
        </div>
      ) : (
        // The rule between two comments in a run. Sighted readers can see
        // the author named above, so at most the time is printed, and not
        // even that when it matches the row above; screen readers read each
        // comment on its own, so the hidden text names the author and the
        // time every time.
        <div className="flex items-center gap-2 py-2">
          <span className="sr-only">
            {authorName}, {relativeTime}
          </span>
          <span className="h-px flex-1 bg-border-subtle" aria-hidden="true" />
          {printsTime && (
            <span className="text-xs text-text-muted" aria-hidden="true">
              {relativeTime}
            </span>
          )}
          {(amplifyToggle || editControls) && (
            <div className="flex items-center gap-1">
              {amplifyToggle}
              {editControls}
            </div>
          )}
        </div>
      )}

      {isEditing ? (
        <div className="space-y-2">
          <FeedbackCodeMirror
            value={editBody}
            onChange={(val) => {
              setEditBody(val);
              if (error) setError(null);
            }}
            onSubmit={handleSave}
            disabled={isPending}
            hasError={!!error}
            autoFocus
          />
          {error && (
            <p className="text-sm text-red-muted" role="alert">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isPending}>
              {isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-sm text-text-secondary">
          <FeedbackBody
            body={feedback.body}
            recordings={recordings}
            recordingName={recordingName}
            onLyricsAnchorClick={onLyricsAnchorClick}
          />
        </div>
      )}
    </div>
  );
}
